import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { theme } from '../../theme';
import { Avatar } from '../ui/Avatar';
import { useGroupChat } from '../../hooks/useGroupChat';
import { withDaySeparators } from '../../utils/chatDay';
import { PollCard, PollData } from './PollCard';

const TrashIcon = Trash2 as React.ComponentType<{ size?: number; color?: string }>;

interface Props {
  groupId: string;
  currentUserId?: string;
  isAdmin?: boolean;
  active: boolean;
  // Encuestas embebidas en el timeline del chat (estilo WhatsApp)
  polls?: PollData[];
  votingPollId?: string | null;
  onVotePoll?: (pollId: string, optionId: string) => void;
  onClosePoll?: (pollId: string) => void;
  onDeletePoll?: (pollId: string) => void;
  onCreatePoll?: () => void;
}

type TimelineItem =
  | { kind: 'message'; key: string; createdAt: string; message: any }
  | { kind: 'poll'; key: string; createdAt: string; poll: PollData }
  | { kind: 'day'; key: string; createdAt: string; label: string };

function timeLabel(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

export const GroupChat: React.FC<Props> = ({
  groupId, currentUserId, isAdmin, active,
  polls, votingPollId, onVotePoll, onClosePoll, onDeletePoll, onCreatePoll,
}) => {
  const { messages, loading, sending, hasMore, loadOlder, send, remove, markRead } = useGroupChat(groupId, active);
  const [text, setText] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);

  // Timeline unificado: mensajes + encuestas ordenados por fecha, con un
  // separador por día intercalado (como WhatsApp).
  const items: TimelineItem[] = useMemo(() => {
    const msgItems: TimelineItem[] = messages.map((m) => ({ kind: 'message', key: `m_${m.id}`, createdAt: m.createdAt, message: m }));
    const pollItems: TimelineItem[] = (polls || []).map((p) => ({ kind: 'poll', key: `p_${p.id}`, createdAt: p.createdAt, poll: p }));
    const ordered = [...msgItems, ...pollItems].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    return withDaySeparators(ordered) as TimelineItem[];
  }, [messages, polls]);

  const isEmpty = items.length === 0;

  // Auto-scroll al fondo cuando llega contenido (si ya estabas abajo).
  useEffect(() => {
    if (atBottomRef.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
    if (active) markRead();
  }, [items, active, markRead]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const handleSend = async () => {
    const t = text.trim();
    if (!t) return;
    setText('');
    atBottomRef.current = true;
    await send(t);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'min(60vh, 520px)', border: `1px solid ${theme.colors.border}`, borderRadius: 14, overflow: 'hidden', background: theme.colors.background }}>
      <div ref={listRef} onScroll={onScroll} style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        {hasMore && (
          <div style={{ textAlign: 'center', marginBottom: 10 }}>
            <button type="button" onClick={loadOlder} style={{ background: 'transparent', border: `1px solid ${theme.colors.border}`, color: theme.colors.textSecondary, borderRadius: 8, padding: '4px 12px', fontSize: 12, cursor: 'pointer' }}>
              Ver mensajes anteriores
            </button>
          </div>
        )}
        {loading && isEmpty ? (
          <p style={{ color: theme.colors.textSecondary, textAlign: 'center' }}>Cargando…</p>
        ) : isEmpty ? (
          <div style={{ textAlign: 'center', color: theme.colors.textSecondary, paddingTop: 32 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>💬</div>
            <div style={{ fontWeight: 600, color: theme.colors.text }}>Todavía no hay mensajes</div>
            <div style={{ fontSize: 13 }}>Arrancá la charla del grupo. Con + creás una encuesta.</div>
          </div>
        ) : (
          items.map((item) => {
            if (item.kind === 'day') {
              return (
                <div key={item.key} style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: theme.colors.textSecondary,
                      background: theme.colors.surface,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: 12,
                      padding: '3px 12px',
                    }}
                  >
                    {item.label}
                  </span>
                </div>
              );
            }
            if (item.kind === 'poll') {
              return (
                <div key={item.key} style={{ marginBottom: 12 }}>
                  <PollCard
                    poll={item.poll}
                    currentUserId={currentUserId}
                    isAdmin={!!isAdmin}
                    voting={votingPollId === item.poll.id}
                    onVote={onVotePoll || (() => {})}
                    onClose={onClosePoll || (() => {})}
                    onDelete={onDeletePoll || (() => {})}
                  />
                </div>
              );
            }
            const m = item.message;
            const mine = m.sender?.id === currentUserId;
            const canDelete = !m.deleted && mine; // solo el autor borra su propio mensaje
            return (
              <div key={item.key} style={{ display: 'flex', flexDirection: mine ? 'row-reverse' : 'row', gap: 8, marginBottom: 10, alignItems: 'flex-end' }}>
                {!mine && <Avatar src={m.sender?.avatar} name={m.sender?.name} size={28} bg={theme.colors.primary} />}
                <div style={{ maxWidth: '75%' }}>
                  {!mine && <div style={{ fontSize: 11, color: theme.colors.textSecondary, marginBottom: 2, marginLeft: 4 }}>{m.sender?.name || 'Alguien'}</div>}
                  <div
                    style={{
                      position: 'relative',
                      background: m.deleted ? 'transparent' : mine ? '#1E3A2E' : theme.colors.surface,
                      color: m.deleted ? theme.colors.textSecondary : mine ? '#EAF7EE' : theme.colors.text,
                      border: m.deleted ? `1px dashed ${theme.colors.border}` : 'none',
                      padding: '8px 12px',
                      borderRadius: 14,
                      fontStyle: m.deleted ? 'italic' : 'normal',
                      fontSize: 14,
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {m.deleted ? 'Mensaje eliminado' : m.content}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => remove(m.id)}
                        title="Borrar mensaje"
                        style={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          width: 22,
                          height: 22,
                          borderRadius: 11,
                          border: `1px solid ${theme.colors.border}`,
                          background: theme.colors.background,
                          color: theme.colors.textSecondary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          padding: 0,
                          opacity: 0.8,
                        }}
                      >
                        <TrashIcon size={12} color={theme.colors.textSecondary} />
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginTop: 2 }}>
                    <span style={{ fontSize: 10, color: theme.colors.textSecondary }}>{timeLabel(m.createdAt)}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, padding: 10, borderTop: `1px solid ${theme.colors.border}` }}>
        {onCreatePoll && (
          <button
            type="button"
            onClick={onCreatePoll}
            title="Crear encuesta"
            style={{ width: 42, flexShrink: 0, borderRadius: 10, border: `1px solid ${theme.colors.border}`, background: 'transparent', color: theme.colors.primary, fontSize: 22, fontWeight: 700, cursor: 'pointer', lineHeight: 1 }}
          >
            +
          </button>
        )}
        <input
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 2000))}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Escribí un mensaje…"
          style={{ flex: 1, boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10, border: `1px solid ${theme.colors.border}`, background: theme.colors.surface, color: theme.colors.text, fontSize: 15 }}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={sending || !text.trim()}
          style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: theme.colors.primary, color: theme.colors.background, fontWeight: 700, fontSize: 14, cursor: sending || !text.trim() ? 'not-allowed' : 'pointer', opacity: !text.trim() ? 0.6 : 1 }}
        >
          Enviar
        </button>
      </div>
    </div>
  );
};
